/* ==========================================================================
   Boreal Luxe — quick add
   --------------------------------------------------------------------------
   Two paths off a single trigger:
     • one variant   -> add straight to cart, no interruption
     • many variants -> open a size/colour dialog, then add

   Deliberately not built on Dawn's quick-add, which fetches and injects a whole
   rendered product section per card. This fetches `/products/<handle>.js`
   (a few KB of JSON) and renders from it.
   ========================================================================== */
(function () {
  if (window.__borealQuickAdd) return;
  window.__borealQuickAdd = true;

  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  /* ---------------------------------------------------------------- helpers */

  var money = (function () {
    var code = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      var fmt = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: code
      });
      return function (cents) { return fmt.format(cents / 100); };
    } catch (e) {
      return function (cents) { return '$' + (cents / 100).toFixed(2); };
    }
  })();

  function announce(message) {
    var region = document.getElementById('bqa-live');
    if (!region) {
      region = document.createElement('div');
      region.id = 'bqa-live';
      region.className = 'bqa__sr';
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    region.textContent = message;
  }

  /* Refresh the cart drawer and the header count from the section HTML that
     cart/add.js returns, then open the drawer. Cheaper and less flickery than
     re-fetching the cart afterwards. */
  function refreshCart(sections) {
    if (!sections) return;

    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      var parsedBubble = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      var bubbleInner = parsedBubble.querySelector('.shopify-section');
      bubble.innerHTML = bubbleInner ? bubbleInner.innerHTML : parsedBubble.body.innerHTML;
    }

    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !sections['cart-drawer']) return;

    var parsedDrawer = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    var fresh = parsedDrawer.getElementById('CartDrawer');
    var current = document.getElementById('CartDrawer');
    if (current && fresh) current.innerHTML = fresh.innerHTML;
    drawer.classList.remove('is-empty');

    setTimeout(function () {
      if (typeof drawer.open === 'function') drawer.open();
      else drawer.classList.add('animate', 'active');
    }, 180);
  }

  function addToCart(variantId) {
    var body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');
    body.append('sections', 'cart-drawer,cart-icon-bubble');

    var url = root + 'cart/add.js';
    // Preview/myshopify domains need this or the POST can be swallowed by the
    // storefront password/redirect layer.
    if (location.hostname.indexOf('.myshopify.com') !== -1) url += '?_fd=0';

    return fetch(url, { method: 'POST', headers: { Accept: 'application/json' }, body: body })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data.status && data.status !== 200) throw new Error(data.description || 'Add to cart failed');
        try { refreshCart(data.sections); } catch (e) { /* cart UI is best-effort */ }
        return data;
      });
  }

  function flash(button, text, className) {
    // Icon triggers hold two SVGs and swap them via the class. Writing
    // textContent here would delete both and leave an empty circle, so the
    // class alone carries the success state for them.
    var isIcon = button.classList.contains('bqa-trigger--icon');
    if (!isIcon) {
      var original = button.dataset.bqaLabel || button.textContent;
      button.dataset.bqaLabel = original;
      button.textContent = text;
    }
    button.classList.add(className);
    setTimeout(function () {
      button.classList.remove(className);
      if (!isIcon) button.textContent = button.dataset.bqaLabel;
    }, 2000);
  }

  /* ----------------------------------------------------------------- dialog */

  var dialog = null;
  var lastTrigger = null;

  function closeDialog() {
    if (!dialog) return;
    var node = dialog;
    dialog = null;
    node.classList.remove('is-open');
    document.documentElement.classList.remove('bqa-locked');
    setTimeout(function () {
      if (node.parentNode) node.parentNode.removeChild(node);
    }, 340);
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
    lastTrigger = null;
  }

  function trapFocus(event) {
    if (!dialog || event.key !== 'Tab') return;
    var focusables = dialog.querySelectorAll(
      'button:not(:disabled), a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  document.addEventListener('keydown', function (event) {
    if (!dialog) return;
    if (event.key === 'Escape') closeDialog();
    trapFocus(event);
  });

  function openDialog(product, url, preselectVariantId, guideUrl) {
    closeDialog();

    var selection = {};
    var start =
      (preselectVariantId &&
        product.variants.filter(function (v) { return String(v.id) === String(preselectVariantId); })[0]) ||
      product.variants.filter(function (v) { return v.available; })[0] ||
      product.variants[0];

    product.options.forEach(function (_option, index) {
      selection[index] = start['option' + (index + 1)];
    });

    function matchingVariant() {
      return (
        product.variants.filter(function (variant) {
          return product.options.every(function (_o, i) {
            return variant['option' + (i + 1)] === selection[i];
          });
        })[0] || null
      );
    }

    // Is this value reachable, holding every *other* currently chosen option?
    function valueAvailable(optionIndex, value) {
      return product.variants.some(function (variant) {
        if (!variant.available) return false;
        if (variant['option' + (optionIndex + 1)] !== value) return false;
        return product.options.every(function (_o, i) {
          return i === optionIndex ? true : variant['option' + (i + 1)] === selection[i];
        });
      });
    }

    dialog = document.createElement('div');
    dialog.className = 'bqa';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', product.title);

    var image = product.featured_image
      ? '<img class="bqa__thumb" src="' + product.featured_image + '&width=200" alt="" loading="lazy">'
      : '';

    dialog.innerHTML =
      '<div class="bqa__scrim"></div>' +
      '<div class="bqa__panel">' +
      '<button type="button" class="bqa__close" aria-label="Close">' +
      '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg>' +
      '</button>' +
      '<div class="bqa__head">' + image +
      '<div><p class="bqa__title">' + product.title + '</p><div class="bqa__price"></div></div>' +
      '</div>' +
      '<div class="bqa__options"></div>' +
      (guideUrl
        ? '<div class="bqa__guide">' +
          '<button type="button" class="bqa__guide-toggle" aria-expanded="false" aria-controls="bqa-guide-body">' +
          '<span>Size guide</span>' +
          '<svg class="bqa__guide-chev" viewBox="0 0 12 8" width="11" height="8" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><path d="M1 1.5L6 6.5L11 1.5"/></svg>' +
          '</button>' +
          '<div class="bqa__guide-panel" id="bqa-guide-body" hidden></div>' +
          '</div>'
        : '') +
      '<button type="button" class="bqa__submit"></button>' +
      '<a class="bqa__details" href="' + url + '">View full details</a>' +
      '</div>';

    document.body.appendChild(dialog);
    document.documentElement.classList.add('bqa-locked');

    var optionsEl = dialog.querySelector('.bqa__options');
    var priceEl = dialog.querySelector('.bqa__price');
    var submit = dialog.querySelector('.bqa__submit');

    function render() {
      optionsEl.innerHTML = '';

      product.options.forEach(function (option, index) {
        var name = typeof option === 'string' ? option : option.name;
        var values = typeof option === 'string' ? [] : option.values;

        var group = document.createElement('div');
        group.className = 'bqa__group';

        var label = document.createElement('div');
        label.className = 'bqa__label';
        label.innerHTML = '<span>' + name + '</span><b>' + selection[index] + '</b>';
        group.appendChild(label);

        var chips = document.createElement('div');
        chips.className = 'bqa__chips';
        chips.setAttribute('role', 'group');
        chips.setAttribute('aria-label', name);

        values.forEach(function (value) {
          var chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'bqa__chip' + (valueAvailable(index, value) ? '' : ' is-unavailable');
          chip.textContent = value;
          chip.setAttribute('aria-pressed', selection[index] === value ? 'true' : 'false');
          chip.addEventListener('click', function () {
            selection[index] = value;
            render();
          });
          chips.appendChild(chip);
        });

        group.appendChild(chips);
        optionsEl.appendChild(group);
      });

      var variant = matchingVariant();
      if (!variant) {
        priceEl.textContent = '';
        submit.disabled = true;
        submit.textContent = 'Unavailable';
        return;
      }

      var markup = money(variant.price);
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        markup += ' <s>' + money(variant.compare_at_price) + '</s>';
      }
      priceEl.innerHTML = markup;
      submit.disabled = !variant.available;
      submit.textContent = variant.available ? 'Add to cart' : 'Sold out';
      submit.dataset.variantId = variant.id;
    }

    render();

    var guideToggle = dialog.querySelector('.bqa__guide-toggle');
    if (guideToggle) {
      var guidePanel = dialog.querySelector('.bqa__guide-panel');
      var guideLoaded = false;
      guideToggle.addEventListener('click', function () {
        var open = guideToggle.getAttribute('aria-expanded') === 'true';
        guideToggle.setAttribute('aria-expanded', open ? 'false' : 'true');
        guidePanel.hidden = open;
        if (open || guideLoaded) return;

        // Fetched on first open, not on dialog open — most shoppers know their
        // size and never ask for it.
        guideLoaded = true;
        guidePanel.innerHTML = '<p class="bqa__guide-status">Loading…</p>';
        fetch(guideUrl, { headers: { Accept: 'text/html' } })
          .then(function (res) {
            if (!res.ok) throw new Error('Size guide fetch failed');
            return res.text();
          })
          .then(function (html) {
            guidePanel.innerHTML = html;
            // Size charts are routinely wider than a 420px dialog. Wrap each one
            // so it scrolls sideways rather than having its last columns clipped.
            guidePanel.querySelectorAll('table').forEach(function (table) {
              var scroller = document.createElement('div');
              scroller.className = 'bqa__guide-scroll';
              table.parentNode.insertBefore(scroller, table);
              scroller.appendChild(table);
            });
          })
          .catch(function () {
            guideLoaded = false;
            guidePanel.innerHTML =
              '<p class="bqa__guide-status"><a href="' +
              guideUrl.split('?')[0] +
              '">Open the size guide</a></p>';
          });
      });
    }

    dialog.querySelector('.bqa__scrim').addEventListener('click', closeDialog);
    dialog.querySelector('.bqa__close').addEventListener('click', closeDialog);

    submit.addEventListener('click', function () {
      if (submit.disabled || submit.classList.contains('is-busy')) return;
      submit.classList.add('is-busy');
      submit.textContent = 'Adding…';
      addToCart(submit.dataset.variantId)
        .then(function () {
          announce(product.title + ' added to cart');
          closeDialog();
        })
        .catch(function () {
          submit.classList.remove('is-busy');
          submit.textContent = 'Sold out';
        });
    });

    // Force a synchronous layout so the browser has the closed state on record,
    // then flip the class in the same task. requestAnimationFrame is NOT safe
    // here: a throttled or backgrounded tab may never run the callback, which
    // leaves the dialog in the DOM at opacity 0 — present but invisible.
    void dialog.offsetHeight;
    dialog.classList.add('is-open');

    var firstChip = dialog.querySelector('.bqa__chip');
    (firstChip || submit).focus();
  }

  /* ---------------------------------------------------------------- binding */

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('.bqa-trigger');
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();
    if (trigger.classList.contains('is-busy')) return;

    var variantId = trigger.getAttribute('data-variant-id');

    // Single variant: straight in, no dialog.
    if (variantId) {
      trigger.classList.add('is-busy');
      addToCart(variantId)
        .then(function () {
          trigger.classList.remove('is-busy');
          announce('Added to cart');
          flash(trigger, trigger.getAttribute('data-added-label') || 'Added', 'is-done');
        })
        .catch(function () {
          trigger.classList.remove('is-busy');
          flash(trigger, 'Unavailable', 'is-done');
        });
      return;
    }

    // Multiple variants: fetch the product JSON and open the dialog.
    var handle = trigger.getAttribute('data-handle');
    var url = trigger.getAttribute('data-url') || root + 'products/' + handle;
    if (!handle) return;

    lastTrigger = trigger;
    trigger.classList.add('is-busy');

    // The card link carries ?variant=… once a colour swatch has been chosen, so
    // the dialog can open on the colour the shopper is actually looking at
    // rather than resetting them to the first one.
    var card = trigger.closest('.card-wrapper');
    var cardLink = card && card.querySelector('a[href*="variant="]');
    var preselect = null;
    if (cardLink) {
      var match = cardLink.getAttribute('href').match(/[?&]variant=(\d+)/);
      if (match) preselect = match[1];
    }

    fetch(root + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Product fetch failed');
        return res.json();
      })
      .then(function (product) {
        trigger.classList.remove('is-busy');
        openDialog(product, url, preselect, trigger.getAttribute('data-guide-url'));
      })
      .catch(function () {
        // Never strand the shopper — fall back to the product page.
        window.location.href = url;
      });
  });
})();
